<!-- dismissible is off while the discard confirm is up: both modals listen for Escape on the
     document, so without this one keypress would answer the confirm and immediately re-ask it. -->
<pm-modal class="pm-acm" [title]="title" [subtitle]="subtitle" size="auto" [busy]="saving"
  [dismissible]="!pendingExit()"
  (closed)="requestExit('closed')">
  <span pmModalIcon class="pm-acm__head-icon" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="10" cy="7.5" r="3.5"/>
      <path d="M3.5 20.5c0-3.6 2.9-6.5 6.5-6.5M17.5 14v6.5M20.75 17.25h-6.5"/>
    </svg>
  </span>

  <!-- Step counter: filled dots plus "done/total", centred in the header. -->
  <span pmModalHeaderCentre class="pm-acm__steps">
    @for (dot of stepDots(); track dot) {
    <span class="pm-acm__step-dot" [class.pm-acm__step-dot--done]="dot < completedSteps"></span>
    }
    <span class="pm-acm__step-count">{{ completedSteps }}/{{ totalSteps }}</span>
  </span>

  <div class="pm-acm__cols">
    <div class="pm-acm__col">
      <pm-form-section title="Client Details" [open]="isSectionOpen('client-details')" (openChange)="setSectionOpen('client-details', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="10" cy="7.5" r="3.5"/><path d="M3 20.5c0-3.6 2.9-6.5 6.5-6.5"/>
            <path d="M17 14.5a3 3 0 1 1 2.2 5 3 3 0 0 1-2.2-5zM18 20.5v1"/>
          </svg>
        </span>

        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">Company/Client ID<i>*</i></span>
            <input class="pm-ff__input" placeholder="Enter client id" [id]="fieldDomId('acm-company-client-id')" [class.pm-ff__input--error]="!!errorFor('acm-company-client-id')" [value]="valueOf('acm-company-client-id')" (input)="setValue('acm-company-client-id', $any($event.target).value)">
            @if (errorFor('acm-company-client-id'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <div class="pm-ff">
            <span class="pm-ff__label">Client Type<i>*</i></span>
            <pm-form-custom-select variant="field" data-field="acm-client-type" dropdownId="acm-client-type" [hasError]="!!errorFor('acm-client-type')"
              [options]="optionsFor('acm-client-type')" [value]="valueOf('acm-client-type')"
              [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
              (valueChange)="setValue('acm-client-type', $event)" />
            @if (errorFor('acm-client-type'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </div>

        <div class="pm-ff">
          <span class="pm-ff__label">Company/Client Name<i>*</i></span>
          <div class="pm-ff__with-btn">
            <input class="pm-ff__input" placeholder="Enter company name" maxlength="50"
              [id]="fieldDomId('acm-company-name')" [class.pm-ff__input--error]="!!errorFor('acm-company-name')"
              [value]="valueOf('acm-company-name')" (input)="setValue('acm-company-name', $any($event.target).value)">
            <button type="button" class="pm-acm__icon-btn" aria-label="Look up on Companies House"
              (click)="lookupCompany.emit()">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="10.5" cy="10.5" r="6.5"/><path d="m21 21-5.6-5.6"/>
              </svg>
            </button>
          </div>
          <span class="pm-ff__hint">Hint: Client name should be maximum of 50 characters</span>
          @if (errorFor('acm-company-name'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </div>

        <!-- Sole Trader and Individual describe a person, not a company. The live Clients form
             swaps these in the same way, off the same Client Type value. -->
        @if (isIndividualType()) {
        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">Individual Client<i>*</i></span>
            <input class="pm-ff__input" placeholder="Individual name" [id]="fieldDomId('acm-individual-client')"
              [class.pm-ff__input--error]="!!errorFor('acm-individual-client')" [value]="valueOf('acm-individual-client')"
              (input)="setValue('acm-individual-client', $any($event.target).value)">
            @if (errorFor('acm-individual-client'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <label class="pm-ff">
            <span class="pm-ff__label">Date of Birth</span>
            <pm-date-picker [inputId]="fieldDomId('acm-dob')" [hasError]="!!errorFor('acm-dob')"
              [value]="valueOf('acm-dob')" (valueChange)="setValue('acm-dob', $event)" />
          </label>
        </div>
        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">National Insurance No</span>
            <input class="pm-ff__input" placeholder="e.g. QQ123456C" [id]="fieldDomId('acm-ni-no')"
              [value]="valueOf('acm-ni-no')" (input)="setValue('acm-ni-no', $any($event.target).value)">
          </label>
          <div class="pm-ff">
            <span class="pm-ff__label">Select company clients</span>
            <pm-form-custom-select variant="field" data-field="acm-company-clients" dropdownId="acm-company-clients"
              [hasError]="!!errorFor('acm-company-clients')"
              [options]="optionsFor('acm-company-clients')" [value]="valueOf('acm-company-clients')"
              [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
              (valueChange)="setValue('acm-company-clients', $event)" />
          </div>
        </div>
        }

        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">Registration No</span>
            <input class="pm-ff__input" placeholder="Enter reg no" [id]="fieldDomId('acm-registration-no')" [class.pm-ff__input--error]="!!errorFor('acm-registration-no')" [value]="valueOf('acm-registration-no')" (input)="setValue('acm-registration-no', $any($event.target).value)">
            @if (errorFor('acm-registration-no'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <label class="pm-ff">
            <span class="pm-ff__label">Website</span>
            <input class="pm-ff__input" placeholder="http://www.test.com" [id]="fieldDomId('acm-website')" [class.pm-ff__input--error]="!!errorFor('acm-website')" [value]="valueOf('acm-website')" (input)="setValue('acm-website', $any($event.target).value)">
            @if (errorFor('acm-website'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
        </div>

        <div class="pm-ff-row">
          <div class="pm-ff">
            <span class="pm-ff__label">Segment</span>
            <pm-form-custom-select variant="field" data-field="acm-segment" dropdownId="acm-segment" [hasError]="!!errorFor('acm-segment')"
              [options]="optionsFor('acm-segment')" [value]="valueOf('acm-segment')"
              [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
              (valueChange)="setValue('acm-segment', $event)" />
            @if (errorFor('acm-segment'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
          <div class="pm-ff">
            <span class="pm-ff__label">Status</span>
            <pm-form-custom-select variant="field" data-field="acm-status" dropdownId="acm-status" [hasError]="!!errorFor('acm-status')"
              [options]="optionsFor('acm-status')" [value]="valueOf('acm-status')"
              [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
              (valueChange)="setValue('acm-status', $event)" />
            @if (errorFor('acm-status'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </div>

        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">Primary Email <i>*</i></span>
            <input class="pm-ff__input" placeholder="Enter email" [id]="fieldDomId('acm-primary-email')" [class.pm-ff__input--error]="!!errorFor('acm-primary-email')" [value]="valueOf('acm-primary-email')" (input)="setValue('acm-primary-email', $any($event.target).value)">
            @if (errorFor('acm-primary-email'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <label class="pm-ff">
            <span class="pm-ff__label">Secondary Email</span>
            <input class="pm-ff__input" placeholder="Enter email" [id]="fieldDomId('acm-secondary-email')" [class.pm-ff__input--error]="!!errorFor('acm-secondary-email')" [value]="valueOf('acm-secondary-email')" (input)="setValue('acm-secondary-email', $any($event.target).value)">
            @if (errorFor('acm-secondary-email'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
        </div>

        <div class="pm-ff-row">
          <div class="pm-ff">
            <span class="pm-ff__label">Phone No</span>
            <pm-phone-input [inputId]="fieldDomId('acm-phone-no')" [hasError]="!!errorFor('acm-phone-no')"
              [countryIso]="valueOf('acm-phone-no-iso')" [value]="valueOf('acm-phone-no')"
              (countryIsoChange)="setValue('acm-phone-no-iso', $event)"
              (valueChange)="setValue('acm-phone-no', $event)" />
            @if (errorFor('acm-phone-no'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
          <div class="pm-ff">
            <span class="pm-ff__label">Secondary Phone No</span>
            <pm-phone-input [inputId]="fieldDomId('acm-secondary-phone-no')" [hasError]="!!errorFor('acm-secondary-phone-no')"
              [countryIso]="valueOf('acm-secondary-phone-no-iso')" [value]="valueOf('acm-secondary-phone-no')"
              (countryIsoChange)="setValue('acm-secondary-phone-no-iso', $event)"
              (valueChange)="setValue('acm-secondary-phone-no', $event)" />
            @if (errorFor('acm-secondary-phone-no'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </div>

        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">Referred By</span>
            <input class="pm-ff__input" placeholder="e.g. John Smith" [id]="fieldDomId('acm-referred-by')" [class.pm-ff__input--error]="!!errorFor('acm-referred-by')" [value]="valueOf('acm-referred-by')" (input)="setValue('acm-referred-by', $any($event.target).value)">
            @if (errorFor('acm-referred-by'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <label class="pm-ff">
            <span class="pm-ff__label">Tags(comma-seperate)</span>
            <input class="pm-ff__input" placeholder="e.g. tag1, tag2" [id]="fieldDomId('acm-tags-comma-seperate')" [class.pm-ff__input--error]="!!errorFor('acm-tags-comma-seperate')" [value]="valueOf('acm-tags-comma-seperate')" (input)="setValue('acm-tags-comma-seperate', $any($event.target).value)">
            @if (errorFor('acm-tags-comma-seperate'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
        </div>

        <pm-form-section title="Bussiness &amp; Tax" variant="nested">
          <div class="pm-ff-row">
            <label class="pm-ff">
              <span class="pm-ff__label">SIC Code</span>
              <input class="pm-ff__input" placeholder="e.g. 8750" [id]="fieldDomId('acm-sic-code')" [class.pm-ff__input--error]="!!errorFor('acm-sic-code')" [value]="valueOf('acm-sic-code')" (input)="setValue('acm-sic-code', $any($event.target).value)">
              @if (errorFor('acm-sic-code'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            <label class="pm-ff">
              <span class="pm-ff__label">Business Start date<i>*</i></span>
              <pm-date-picker [inputId]="fieldDomId('acm-business-start-date')" [hasError]="!!errorFor('acm-business-start-date')"
                [value]="valueOf('acm-business-start-date')" (valueChange)="setValue('acm-business-start-date', $event)" />
              @if (errorFor('acm-business-start-date'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
          </div>
          <div class="pm-ff-row">
            <label class="pm-ff">
              <span class="pm-ff__label">Book Start Date<i>*</i></span>
              <pm-date-picker [inputId]="fieldDomId('acm-book-start-date')" [hasError]="!!errorFor('acm-book-start-date')"
                [value]="valueOf('acm-book-start-date')" (valueChange)="setValue('acm-book-start-date', $event)" />
              @if (errorFor('acm-book-start-date'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            <label class="pm-ff">
              <span class="pm-ff__label">Year End Date</span>
              <pm-date-picker [inputId]="fieldDomId('acm-year-end-date')" [hasError]="!!errorFor('acm-year-end-date')"
                [value]="valueOf('acm-year-end-date')" (valueChange)="setValue('acm-year-end-date', $event)" />
              @if (errorFor('acm-year-end-date'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
          </div>
          <div class="pm-ff-row">
            <label class="pm-ff">
              <span class="pm-ff__label">IDV Refernce</span>
              <input class="pm-ff__input" placeholder="Identity verification" [id]="fieldDomId('acm-idv-refernce')" [class.pm-ff__input--error]="!!errorFor('acm-idv-refernce')" [value]="valueOf('acm-idv-refernce')" (input)="setValue('acm-idv-refernce', $any($event.target).value)">
              @if (errorFor('acm-idv-refernce'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            <label class="pm-ff">
              <span class="pm-ff__label">UTR No</span>
              <input class="pm-ff__input" placeholder="Identity verification" [id]="fieldDomId('acm-utr-no')" [class.pm-ff__input--error]="!!errorFor('acm-utr-no')" [value]="valueOf('acm-utr-no')" (input)="setValue('acm-utr-no', $any($event.target).value)">
              @if (errorFor('acm-utr-no'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
          </div>
        </pm-form-section>

        <pm-form-section title="VAT Details" variant="nested">
          <div class="pm-ff-row">
            <div class="pm-ff">
              <span class="pm-ff__label">VAT Scheme</span>
              <pm-form-custom-select variant="field" data-field="acm-vat-scheme" dropdownId="acm-vat-scheme" [hasError]="!!errorFor('acm-vat-scheme')"
                [options]="optionsFor('acm-vat-scheme')" [value]="valueOf('acm-vat-scheme')"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setValue('acm-vat-scheme', $event)" />
              @if (errorFor('acm-vat-scheme'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
            <div class="pm-ff">
              <span class="pm-ff__label">VAT Submit Type</span>
              <pm-form-custom-select variant="field" data-field="acm-vat-submit" dropdownId="acm-vat-submit" [hasError]="!!errorFor('acm-vat-submit')"
                [options]="optionsFor('acm-vat-submit')" [value]="valueOf('acm-vat-submit')"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setValue('acm-vat-submit', $event)" />
              @if (errorFor('acm-vat-submit'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
          </div>
          <div class="pm-ff-row">
            <label class="pm-ff">
              <span class="pm-ff__label">VAT Registration No</span>
              <input class="pm-ff__input" placeholder="123/P12345" [id]="fieldDomId('acm-vat-registration-no')" [class.pm-ff__input--error]="!!errorFor('acm-vat-registration-no')" [value]="valueOf('acm-vat-registration-no')" (input)="setValue('acm-vat-registration-no', $any($event.target).value)">
              @if (errorFor('acm-vat-registration-no'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            <label class="pm-ff">
              <span class="pm-ff__label">VAT Registration Date</span>
              <pm-date-picker [inputId]="fieldDomId('acm-vat-registration-date')" [hasError]="!!errorFor('acm-vat-registration-date')"
                [value]="valueOf('acm-vat-registration-date')" (valueChange)="setValue('acm-vat-registration-date', $event)" />
              @if (errorFor('acm-vat-registration-date'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
          </div>
        </pm-form-section>

        <pm-form-section title="PAYE/Payroll" variant="nested">
          <div class="pm-ff-row">
            <label class="pm-ff">
              <span class="pm-ff__label">PAYE Reference</span>
              <input class="pm-ff__input" placeholder="e.g. 8750" [id]="fieldDomId('acm-paye-reference')" [class.pm-ff__input--error]="!!errorFor('acm-paye-reference')" [value]="valueOf('acm-paye-reference')" (input)="setValue('acm-paye-reference', $any($event.target).value)">
              @if (errorFor('acm-paye-reference'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            <label class="pm-ff">
              <span class="pm-ff__label">Account Office Ref No</span>
              <input class="pm-ff__input" placeholder="e.g.1234567" [id]="fieldDomId('acm-account-office-ref-no')" [class.pm-ff__input--error]="!!errorFor('acm-account-office-ref-no')" [value]="valueOf('acm-account-office-ref-no')" (input)="setValue('acm-account-office-ref-no', $any($event.target).value)">
              @if (errorFor('acm-account-office-ref-no'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
          </div>
        </pm-form-section>

        <pm-form-section title="Billing &amp; System" variant="nested">
          <div class="pm-ff-row">
            <div class="pm-ff">
              <span class="pm-ff__label">Accounting System<i>*</i></span>
              <pm-form-custom-select variant="field" data-field="acm-acc-system" dropdownId="acm-acc-system" [hasError]="!!errorFor('acm-acc-system')"
                [options]="optionsFor('acm-acc-system')" [value]="valueOf('acm-acc-system')"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setValue('acm-acc-system', $event)" />
              @if (errorFor('acm-acc-system'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
            <!-- "Other" turns the system into free text, exactly as the live Clients form does. -->
            @if (isOtherAccountingSystem()) {
            <label class="pm-ff">
              <span class="pm-ff__label">Specify Accounting System<i>*</i></span>
              <input class="pm-ff__input" placeholder="Name the system" [id]="fieldDomId('acm-acc-system-other')"
                [class.pm-ff__input--error]="!!errorFor('acm-acc-system-other')" [value]="valueOf('acm-acc-system-other')"
                (input)="setValue('acm-acc-system-other', $any($event.target).value)">
              @if (errorFor('acm-acc-system-other'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            }
            <div class="pm-ff">
              <span class="pm-ff__label">Billing Cycle<i>*</i></span>
              <pm-form-custom-select variant="field" data-field="acm-billing-cycle" dropdownId="acm-billing-cycle" [hasError]="!!errorFor('acm-billing-cycle')"
                [options]="optionsFor('acm-billing-cycle')" [value]="valueOf('acm-billing-cycle')"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setValue('acm-billing-cycle', $event)" />
              @if (errorFor('acm-billing-cycle'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
          </div>
          <div class="pm-ff-row">
            <div class="pm-ff">
              <span class="pm-ff__label">Source<i>*</i></span>
              <pm-form-custom-select variant="field" data-field="acm-source" dropdownId="acm-source" [hasError]="!!errorFor('acm-source')"
                [options]="optionsFor('acm-source')" [value]="valueOf('acm-source')"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setValue('acm-source', $event)" />
              @if (errorFor('acm-source'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
            <label class="pm-ff">
              <span class="pm-ff__label">Internal Reference</span>
              <input class="pm-ff__input" placeholder="Enter reference" [id]="fieldDomId('acm-internal-reference')" [class.pm-ff__input--error]="!!errorFor('acm-internal-reference')" [value]="valueOf('acm-internal-reference')" (input)="setValue('acm-internal-reference', $any($event.target).value)">
              @if (errorFor('acm-internal-reference'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
          </div>
        </pm-form-section>
      </pm-form-section>

      <pm-form-section title="Address" [open]="isSectionOpen('address')" (openChange)="setSectionOpen('address', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a7 7 0 0 1 7 7c0 5-7 11-7 11S5 14 5 9a7 7 0 0 1 7-7z"/>
            <circle cx="12" cy="9" r="2.3"/>
          </svg>
        </span>

        <div class="pm-ff">
          <span class="pm-ff__label">Address Type<i>*</i></span>
          <pm-form-custom-select variant="field" data-field="acm-address-type" dropdownId="acm-address-type"
            [hasError]="!!errorFor('acm-address-type')"
            [options]="optionsFor('acm-address-type')" [value]="valueOf('acm-address-type')"
            [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
            (valueChange)="setValue('acm-address-type', $event)" />
          @if (errorFor('acm-address-type'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </div>
        <label class="pm-ff">
          <span class="pm-ff__label">Address Line 1<i>*</i></span>
          <input class="pm-ff__input" placeholder="Contact name" [id]="fieldDomId('acm-address-line-1')" [class.pm-ff__input--error]="!!errorFor('acm-address-line-1')" [value]="valueOf('acm-address-line-1')" (input)="setValue('acm-address-line-1', $any($event.target).value)">
          @if (errorFor('acm-address-line-1'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </label>
        <label class="pm-ff">
          <span class="pm-ff__label">Address Line 2</span>
          <input class="pm-ff__input" placeholder="Contact name" [id]="fieldDomId('acm-address-line-2')" [class.pm-ff__input--error]="!!errorFor('acm-address-line-2')" [value]="valueOf('acm-address-line-2')" (input)="setValue('acm-address-line-2', $any($event.target).value)">
          @if (errorFor('acm-address-line-2'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </label>
        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">City/Town<i>*</i></span>
            <input class="pm-ff__input" placeholder="Company city/town" [id]="fieldDomId('acm-city-town')" [class.pm-ff__input--error]="!!errorFor('acm-city-town')" [value]="valueOf('acm-city-town')" (input)="setValue('acm-city-town', $any($event.target).value)">
            @if (errorFor('acm-city-town'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <label class="pm-ff">
            <span class="pm-ff__label">Post code<i>*</i></span>
            <input class="pm-ff__input" placeholder="Company post code" [id]="fieldDomId('acm-post-code')" [class.pm-ff__input--error]="!!errorFor('acm-post-code')" [value]="valueOf('acm-post-code')" (input)="setValue('acm-post-code', $any($event.target).value)">
            @if (errorFor('acm-post-code'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
        </div>
        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">County<i>*</i></span>
            <input class="pm-ff__input" placeholder="Company city/town" [id]="fieldDomId('acm-county')" [class.pm-ff__input--error]="!!errorFor('acm-county')" [value]="valueOf('acm-county')" (input)="setValue('acm-county', $any($event.target).value)">
            @if (errorFor('acm-county'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <div class="pm-ff">
            <span class="pm-ff__label">Country<i>*</i></span>
            <pm-form-custom-select variant="field" data-field="acm-country" dropdownId="acm-country" [hasError]="!!errorFor('acm-country')"
              [options]="optionsFor('acm-country')" [value]="valueOf('acm-country')"
              [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
              (valueChange)="setValue('acm-country', $event)" />
            @if (errorFor('acm-country'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </div>
      </pm-form-section>

      <pm-form-section title="Due Diligence" [open]="isSectionOpen('due-diligence')" (openChange)="setSectionOpen('due-diligence', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2.5 4 6v6c0 5 8 9.5 8 9.5s8-4.5 8-9.5V6z"/><path d="m9 12 2.2 2.2L15.5 10"/>
          </svg>
        </span>

        <pm-form-section title="ID Check" variant="nested">
          <label class="pm-ff">
            <span class="pm-ff__label">Date</span>
            <pm-date-picker [inputId]="fieldDomId('acm-date')" [hasError]="!!errorFor('acm-date')"
              [value]="valueOf('acm-date')" (valueChange)="setValue('acm-date', $event)" />
            @if (errorFor('acm-date'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
        </pm-form-section>
        <pm-form-section title="AML Check" variant="nested">
          <div class="pm-ff">
            <span class="pm-ff__label">Status</span>
            <pm-form-custom-select variant="field" data-field="acm-aml-status" dropdownId="acm-aml-status" [hasError]="!!errorFor('acm-aml-status')"
              [options]="optionsFor('acm-aml-status')" [value]="valueOf('acm-aml-status')"
              [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
              (valueChange)="setValue('acm-aml-status', $event)" />
            @if (errorFor('acm-aml-status'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </pm-form-section>
        <pm-form-section title="Proposal Letter" variant="nested">
          <label class="pm-ff">
            <span class="pm-ff__label">Date sent</span>
            <pm-date-picker [inputId]="fieldDomId('acm-date-sent')" [hasError]="!!errorFor('acm-date-sent')"
              [value]="valueOf('acm-date-sent')" (valueChange)="setValue('acm-date-sent', $event)" />
            @if (errorFor('acm-date-sent'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
        </pm-form-section>
        <pm-form-section title="Letter of Engagement" variant="nested">
          <label class="pm-ff">
            <span class="pm-ff__label">Date Signed</span>
            <pm-date-picker [inputId]="fieldDomId('acm-date-signed')" [hasError]="!!errorFor('acm-date-signed')"
              [value]="valueOf('acm-date-signed')" (valueChange)="setValue('acm-date-signed', $event)" />
            @if (errorFor('acm-date-signed'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
        </pm-form-section>
      </pm-form-section>
    </div>

    <div class="pm-acm__col">
      <pm-form-section title="Contact Person" [open]="isSectionOpen('contact-person')" (openChange)="setSectionOpen('contact-person', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="3.5"/><path d="M5 20.5c0-3.9 3.1-7 7-7s7 3.1 7 7"/>
          </svg>
        </span>

        <div class="pm-ff">
          <span class="pm-ff__label">Select Contact<i>*</i></span>
          <pm-form-custom-select variant="field" data-field="acm-contact" dropdownId="acm-contact" [showAvatar]="true" [hasError]="!!errorFor('acm-contact')" placeholder="Select Contact"
            [options]="optionsFor('acm-contact')" [value]="valueOf('acm-contact')"
            [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
            (valueChange)="setValue('acm-contact', $event)" />
          @if (errorFor('acm-contact'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </div>
        <label class="pm-ff">
          <span class="pm-ff__label">Contact Name<i>*</i></span>
          <input class="pm-ff__input" placeholder="Contact name" [id]="fieldDomId('acm-contact-name')" [class.pm-ff__input--error]="!!errorFor('acm-contact-name')" [value]="valueOf('acm-contact-name')" (input)="setValue('acm-contact-name', $any($event.target).value)">
          @if (errorFor('acm-contact-name'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </label>
        <div class="pm-ff-row">
          <label class="pm-ff">
            <span class="pm-ff__label">Contact Email<i>*</i></span>
            <input class="pm-ff__input" placeholder="Company email..." [id]="fieldDomId('acm-contact-email')" [class.pm-ff__input--error]="!!errorFor('acm-contact-email')" [value]="valueOf('acm-contact-email')" (input)="setValue('acm-contact-email', $any($event.target).value)">
            @if (errorFor('acm-contact-email'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>
          <div class="pm-ff">
            <span class="pm-ff__label">Contact Phone No<i>*</i></span>
            <pm-phone-input [inputId]="fieldDomId('acm-contact-phone-no')" [hasError]="!!errorFor('acm-contact-phone-no')"
              [countryIso]="valueOf('acm-contact-phone-no-iso')" [value]="valueOf('acm-contact-phone-no')"
              (countryIsoChange)="setValue('acm-contact-phone-no-iso', $event)"
              (valueChange)="setValue('acm-contact-phone-no', $event)" />
            @if (errorFor('acm-contact-phone-no'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </div>
      </pm-form-section>

      <!-- Directors & Shareholders. Not in the Figma spec, carried over from the live Clients form:
           a client can have any number of officers, each with their own appointment and termination
           dates, and dropping the block would stop that being captured at all. -->
      <pm-form-section title="Directors &amp; Shareholders" [open]="isSectionOpen('directors-and-shareholders')" (openChange)="setSectionOpen('directors-and-shareholders', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="8" r="3.2"/>
            <path d="M3 20a6 6 0 0 1 12 0M17 11.2A3.2 3.2 0 1 0 15.4 5M18 20a5.6 5.6 0 0 0-2.2-4.4"/>
          </svg>
        </span>

        @for (o of officers(); track o.key; let i = $index) {
        <div class="pm-acm__officer">
          <div class="pm-acm__officer-head">
            <span class="pm-acm__officer-no">Person {{ i + 1 }}</span>
            <button type="button" class="pm-acm__officer-remove" [disabled]="officers().length <= 1"
              [attr.aria-label]="'Remove person ' + (i + 1)" (click)="removeOfficer(o.key)">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>
            </button>
          </div>

          <div class="pm-ff-row">
            @let nameId = officerFieldId(o.key, 'name');
            <label class="pm-ff">
              <span class="pm-ff__label">Name@if (officersRequired()) {<i>*</i>}</span>
              <input class="pm-ff__input" placeholder="Full name" [id]="fieldDomId(nameId)"
                [class.pm-ff__input--error]="!!errorFor(nameId)" [value]="o.name"
                (input)="setOfficerField(o.key, 'name', $any($event.target).value)">
              @if (errorFor(nameId); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            @let emailId = officerFieldId(o.key, 'email');
            <label class="pm-ff">
              <span class="pm-ff__label">Email@if (officersRequired()) {<i>*</i>}</span>
              <input class="pm-ff__input" placeholder="Email address" [id]="fieldDomId(emailId)"
                [class.pm-ff__input--error]="!!errorFor(emailId)" [value]="o.email"
                (input)="setOfficerField(o.key, 'email', $any($event.target).value)">
              @if (errorFor(emailId); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
          </div>

          @let addressId = officerFieldId(o.key, 'address');
          <label class="pm-ff">
            <span class="pm-ff__label">Address@if (officersRequired()) {<i>*</i>}</span>
            <input class="pm-ff__input" placeholder="Address" [id]="fieldDomId(addressId)"
              [class.pm-ff__input--error]="!!errorFor(addressId)" [value]="o.address"
              (input)="setOfficerField(o.key, 'address', $any($event.target).value)">
            @if (errorFor(addressId); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </label>

          <div class="pm-ff-row">
            @let mobileId = officerFieldId(o.key, 'mobile');
            <div class="pm-ff">
              <span class="pm-ff__label">Mobile@if (officersRequired()) {<i>*</i>}</span>
              <pm-phone-input [inputId]="fieldDomId(mobileId)" [hasError]="!!errorFor(mobileId)"
                [value]="o.mobile" [countryIso]="o.country || 'gb'"
                (valueChange)="setOfficerField(o.key, 'mobile', $event)" />
              @if (errorFor(mobileId); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
            @let countryId = officerFieldId(o.key, 'country');
            <div class="pm-ff">
              <span class="pm-ff__label">Country@if (officersRequired()) {<i>*</i>}</span>
              <pm-form-custom-select variant="field" [attr.data-field]="countryId" [dropdownId]="'acm-officer-country-' + o.key"
                [hasError]="!!errorFor(countryId)"
                [options]="optionsFor('acm-officer-country')" [value]="o.country"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setOfficerField(o.key, 'country', $event)" />
              @if (errorFor(countryId); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
          </div>

          <div class="pm-ff-row">
            @let appointedId = officerFieldId(o.key, 'appointedOn');
            <label class="pm-ff" [attr.data-field]="appointedId">
              <span class="pm-ff__label">Appointment Date@if (officersRequired()) {<i>*</i>}</span>
              <pm-date-picker [hasError]="!!errorFor(appointedId)" [value]="o.appointedOn"
                (valueChange)="setOfficerField(o.key, 'appointedOn', $event)" />
              @if (errorFor(appointedId); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </label>
            <label class="pm-ff">
              <span class="pm-ff__label">Termination Date</span>
              <pm-date-picker [value]="o.terminatedOn" (valueChange)="setOfficerField(o.key, 'terminatedOn', $event)" />
            </label>
          </div>

          <button type="button" class="pm-acm__check" [attr.aria-pressed]="o.shareholder"
            (click)="setOfficerField(o.key, 'shareholder', !o.shareholder)">
            <span class="pm-acm__check-box" [class.pm-acm__check-box--on]="o.shareholder">
              <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor"
                stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="m20 6-11 11-5-5"/>
              </svg>
            </span>
            <span class="pm-acm__check-label">Shareholder</span>
          </button>
        </div>
        }

        <button type="button" class="pm-acm__btn pm-acm__btn--outline pm-acm__officer-add" (click)="addOfficer()">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          Add person
        </button>

      </pm-form-section>

      <pm-form-section title="Services &amp; Staff" [open]="isSectionOpen('services-and-staff')" (openChange)="setSectionOpen('services-and-staff', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m12 3 8 4-8 4-8-4z"/><path d="m4 12 8 4 8-4"/><path d="m4 17 8 4 8-4"/>
          </svg>
        </span>

        <div class="pm-ff">
          <span class="pm-ff__label">Select Services<i>*</i></span>
          <div class="pm-acm__services" data-field="acm-select-services" tabindex="-1"
            [class.pm-acm__services--error]="!!errorFor('acm-select-services')">
            @for (s of services; track s.id) {
            <button type="button" class="pm-acm__check" [attr.aria-pressed]="isServiceSelected(s.id)"
              (click)="toggleService(s.id)">
              <span class="pm-acm__check-box" [class.pm-acm__check-box--on]="isServiceSelected(s.id)">
                <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor"
                  stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="m20 6-11 11-5-5"/>
                </svg>
              </span>
              <span class="pm-acm__check-label">{{ s.label }}</span>
            </button>
            }
          </div>

          @if (errorFor('acm-select-services'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </div>

        <pm-form-section title="Staff &amp; Assignment" variant="nested">
          <div class="pm-ff-row">
            <div class="pm-ff">
              <span class="pm-ff__label">Client Manager</span>
              <pm-form-custom-select variant="field" data-field="acm-manager" dropdownId="acm-manager" [hasError]="!!errorFor('acm-manager')"
                [options]="optionsFor('acm-manager')" [value]="valueOf('acm-manager')"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setValue('acm-manager', $event)" />
              @if (errorFor('acm-manager'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
            <div class="pm-ff">
              <span class="pm-ff__label">Accountant / Staff</span>
              <pm-form-custom-select variant="field" data-field="acm-staff" dropdownId="acm-staff" [hasError]="!!errorFor('acm-staff')"
                [options]="optionsFor('acm-staff')" [value]="valueOf('acm-staff')"
                [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
                (valueChange)="setValue('acm-staff', $event)" />
              @if (errorFor('acm-staff'); as err) { <span class="pm-ff__error">{{ err }}</span> }
            </div>
          </div>
          <div class="pm-ff">
            <span class="pm-ff__label">Partner</span>
            <pm-form-custom-select variant="field" data-field="acm-partner" dropdownId="acm-partner" [hasError]="!!errorFor('acm-partner')"
              [options]="optionsFor('acm-partner')" [value]="valueOf('acm-partner')"
              [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
              (valueChange)="setValue('acm-partner', $event)" />
            @if (errorFor('acm-partner'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </pm-form-section>


      </pm-form-section>

      <pm-form-section title="Portal Access" [open]="isSectionOpen('portal-access')" (openChange)="setSectionOpen('portal-access', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10 3H4v18h6"/><path d="M14 12h7M18 8.5l3.5 3.5L18 15.5"/>
          </svg>
        </span>

        <button type="button" class="pm-acm__check" [attr.aria-pressed]="createPortalUser()"
          (click)="togglePortalUser()">
          <span class="pm-acm__check-box" [class.pm-acm__check-box--on]="createPortalUser()">
            <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor"
              stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="m20 6-11 11-5-5"/>
            </svg>
          </span>
          <span class="pm-acm__check-label pm-acm__check-label--accent">Create Client User from this list</span>
        </button>

        <!-- The credential fields the live Clients form collects when a portal user is created
             alongside the client. Hidden until the box is ticked: they are meaningless otherwise,
             and a password field on a form that is not creating a login invites one to be typed. -->
        @if (createPortalUser()) {
        <div class="pm-ff">
          <span class="pm-ff__label">Select User</span>
          <pm-form-custom-select variant="field" data-field="acm-portal-user" dropdownId="acm-portal-user"
            [options]="optionsFor('acm-portal-user')" [value]="valueOf('acm-portal-user')"
            [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
            (valueChange)="setValue('acm-portal-user', $event)" />
        </div>
        <div class="pm-ff-row">
          <div class="pm-ff">
            <span class="pm-ff__label">Password<i>*</i></span>
            <div class="pm-ff__pw">
              <input class="pm-ff__input pm-ff__input--pw" autocomplete="new-password" placeholder="Enter password"
                [type]="isPasswordRevealed('acm-portal-password') ? 'text' : 'password'"
                [id]="fieldDomId('acm-portal-password')"
                [class.pm-ff__input--error]="!!errorFor('acm-portal-password')" [value]="valueOf('acm-portal-password')"
                (input)="setValue('acm-portal-password', $any($event.target).value)">
              <button type="button" class="pm-ff__pw-eye"
                [attr.aria-pressed]="isPasswordRevealed('acm-portal-password')"
                [attr.aria-label]="isPasswordRevealed('acm-portal-password') ? 'Hide password' : 'Show password'"
                [title]="isPasswordRevealed('acm-portal-password') ? 'Hide' : 'Show'"
                (click)="togglePasswordReveal('acm-portal-password')">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z"/>
                  <circle cx="12" cy="12" r="3"/>
                  @if (isPasswordRevealed('acm-portal-password')) { <path d="M4 20 20 4"/> }
                </svg>
              </button>
            </div>
            @if (errorFor('acm-portal-password'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
          <div class="pm-ff">
            <span class="pm-ff__label">Confirm Password<i>*</i></span>
            <div class="pm-ff__pw">
              <input class="pm-ff__input pm-ff__input--pw" autocomplete="new-password" placeholder="Re-enter password"
                [type]="isPasswordRevealed('acm-portal-password-confirm') ? 'text' : 'password'"
                [id]="fieldDomId('acm-portal-password-confirm')"
                [class.pm-ff__input--error]="!!errorFor('acm-portal-password-confirm')" [value]="valueOf('acm-portal-password-confirm')"
                (input)="setValue('acm-portal-password-confirm', $any($event.target).value)">
              <button type="button" class="pm-ff__pw-eye"
                [attr.aria-pressed]="isPasswordRevealed('acm-portal-password-confirm')"
                [attr.aria-label]="isPasswordRevealed('acm-portal-password-confirm') ? 'Hide password' : 'Show password'"
                [title]="isPasswordRevealed('acm-portal-password-confirm') ? 'Hide' : 'Show'"
                (click)="togglePasswordReveal('acm-portal-password-confirm')">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z"/>
                  <circle cx="12" cy="12" r="3"/>
                  @if (isPasswordRevealed('acm-portal-password-confirm')) { <path d="M4 20 20 4"/> }
                </svg>
              </button>
            </div>
            @if (errorFor('acm-portal-password-confirm'); as err) { <span class="pm-ff__error">{{ err }}</span> }
          </div>
        </div>
        }

        <div class="pm-ff">
          <span class="pm-ff__label">Partner Role</span>
          <pm-form-custom-select variant="field" data-field="acm-partner-role" dropdownId="acm-partner-role" [hasError]="!!errorFor('acm-partner-role')"
            [options]="optionsFor('acm-partner-role')" [value]="valueOf('acm-partner-role')"
            [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
            (valueChange)="setValue('acm-partner-role', $event)" />
          @if (errorFor('acm-partner-role'); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </div>

      </pm-form-section>

      <!-- Additional Fields: whatever the practice has configured. Rendered only when there are
           any - a heading over an empty section is worse than no heading. -->
      @if (customFields.length > 0) {
      <pm-form-section title="Additional Fields" [open]="isSectionOpen('additional-fields')"
        (openChange)="setSectionOpen('additional-fields', $event)">
        <span pmFormSectionIcon class="pm-fsec-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
            stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 5v14M5 12h14"/>
          </svg>
        </span>

        @for (field of customFields; track field.id) {
        <div class="pm-ff">
          <span class="pm-ff__label">{{ field.name }}@if (field.required) {<i>*</i>}</span>

          @if (field.fieldType === 'dropdown') {
          <pm-form-custom-select variant="field" [dropdownId]="'acm-custom-' + field.id"
            [hasError]="!!customFieldError(field.id)"
            [options]="customFieldOptions(field)" [value]="customFieldValue(field.id)"
            [activeDropdown]="openDropdown()" (toggleRequest)="onDropdownToggle($event)"
            (valueChange)="setCustomFieldValue(field.id, $event)" />
          } @else if (field.fieldType === 'date') {
          <pm-date-picker [inputId]="customFieldDomId(field.id)" [hasError]="!!customFieldError(field.id)"
            [value]="customFieldValue(field.id)" (valueChange)="setCustomFieldValue(field.id, $event)" />
          } @else {
          <input class="pm-ff__input" [id]="customFieldDomId(field.id)"
            [type]="field.fieldType === 'number' ? 'number' : 'text'"
            [placeholder]="field.description || 'Enter ' + field.name"
            [class.pm-ff__input--error]="!!customFieldError(field.id)"
            [value]="customFieldValue(field.id)"
            (input)="setCustomFieldValue(field.id, $any($event.target).value)">
          }

          @if (customFieldError(field.id); as err) { <span class="pm-ff__error">{{ err }}</span> }
        </div>
        }

      </pm-form-section>
      }
    </div>
  </div>

  <ng-container pmModalFooter>
    <!-- Said next to the button that was pressed: the first gap is often several sections away,
         and a scroll on its own reads as nothing having happened. -->
    @if (blockedCount() > 0) {
    <span class="pm-acm__blocked" role="status">
      {{ blockedCount() }} {{ blockedCount() === 1 ? "field needs" : "fields need" }} attention
    </span>
    }
    <button type="button" class="pm-acm__btn pm-acm__btn--ghost" [disabled]="saving" (click)="requestExit('cancelled')">Cancel</button>
    <button type="button" class="pm-acm__btn pm-acm__btn--outline" [disabled]="saving" (click)="savedDraft.emit()">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
        stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z"/><path d="M14 3v5h5"/>
      </svg>
      Save Draft
    </button>
    <button type="button" class="pm-acm__btn pm-acm__btn--primary" [disabled]="saving" (click)="onSave()">
      @if (saving) {
      <svg class="pm-acm__spinner" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-opacity="0.3" stroke-width="3"/>
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
      </svg>
      Saving…
      } @else {
      Save Client
      }
    </button>
  </ng-container>
</pm-modal>

@if (pendingExit()) {
<pm-confirm-dialog
  title="Discard your changes?"
  message="This client has not been saved. Anything you have entered will be lost."
  icon="warning"
  tone="danger"
  confirmLabel="Discard"
  cancelLabel="Keep editing"
  (confirmed)="confirmExit()"
  (cancelled)="cancelExit()" />
}
